(() => {
  const prefersReduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

  // ---------- Boot flags / page enter ----------
  function boot() {
    document.documentElement.classList.add('anim-ready');
    document.body.classList.add('page-enter');
    // Remove page-enter after animation so it does not re-run on layout changes
    setTimeout(() => document.body.classList.remove('page-enter'), 1000);
  }

  // ---------- Progress bar ----------
  function initProgressBar() {
    if (prefersReduced) return;

    const bar = document.createElement('div');
    bar.className = 'scroll-progress';
    document.body.appendChild(bar);

    let ticking = false;
    function update() {
      ticking = false;
      const doc = document.documentElement;
      const max = Math.max(1, doc.scrollHeight - doc.clientHeight);
      const p = Math.min(1, Math.max(0, (window.scrollY || 0) / max));
      bar.style.transform = `scaleX(${p})`;
    }
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    update();
  }

  // ---------- Reveal on scroll ----------
  function setRevealDelay(el) {
    const d = el.getAttribute('data-reveal-delay');
    if (!d) return;
    const ms = Number(d);
    if (!Number.isFinite(ms)) return;
    el.style.setProperty('--reveal-delay', `${ms}ms`);
  }

  function initReveals(root = document) {
    const els = Array.from(root.querySelectorAll('[data-reveal]'));
    if (!els.length) return;

    els.forEach(setRevealDelay);

    if (prefersReduced || !('IntersectionObserver' in window)) {
      els.forEach(el => el.classList.add('is-in'));
      return;
    }

    const io = new IntersectionObserver((entries, obs) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        e.target.classList.add('is-in');
        obs.unobserve(e.target);
      }
    }, { threshold: 0.16, rootMargin: '0px 0px -8% 0px' });

    els.forEach(el => io.observe(el));
  }

  // ---------- Auto-tag common elements ----------
  function autoTagCommon() {
    // Tag only if not already tagged
    const candidates = document.querySelectorAll(
      'main h1, main h2, main h3, main .button, main .button-secondary, main .card, main .product-card, main .collection-card, main .media, main img'
    );

    let delay = 0;
    const step = 50;
    const max = 200;

    candidates.forEach((el) => {
      if (el.hasAttribute('data-reveal')) return;
      // Avoid tagging tiny UI pieces in header/footer
      if (el.closest('#header-group') || el.closest('footer')) return;

      const tag = el.matches('img, .media') ? 'up' : 'left';

      el.setAttribute('data-reveal', tag);
      el.setAttribute('data-reveal-delay', String(delay));
      delay = (delay + step) % max;
    });
  }

  // ---------- Header hide on scroll down, show on scroll up ----------
  function initHeaderSmartHide() {
    let lastY = window.scrollY || 0;
    let ticking = false;

    function update() {
      ticking = false;
      const y = window.scrollY || 0;

      // Add scrolled state
      if (y > 10) document.body.classList.add('has-scrolled');
      else document.body.classList.remove('has-scrolled');

      // Hide/show header
      const goingDown = y > lastY;
      const nearTop = y < 40;
      const bigMove = Math.abs(y - lastY) > 8;

      if (!prefersReduced && !nearTop && bigMove && goingDown) {
        document.body.classList.add('header-hidden');
      } else if (bigMove && (!goingDown || nearTop)) {
        document.body.classList.remove('header-hidden');
      }

      lastY = y;
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    update();
  }

  // ---------- Back to top button ----------
  function initBackToTop() {
    const btn = document.createElement('button');
    btn.className = 'back-to-top';
    btn.type = 'button';
    btn.textContent = 'Top';
    btn.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: prefersReduced ? 'auto' : 'smooth' });
    });
    document.body.appendChild(btn);

    let ticking = false;
    function update() {
      ticking = false;
      const y = window.scrollY || 0;
      if (y > 600) btn.classList.add('is-visible');
      else btn.classList.remove('is-visible');
    }
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    update();
  }

  // ---------- Cursor spotlight (optional) ----------
  function initSpotlight() {
    // Enable only on desktop-ish pointers
    const finePointer = window.matchMedia?.('(pointer:fine)')?.matches;
    if (!finePointer || prefersReduced) return;

    const overlay = document.createElement('div');
    overlay.className = 'spotlight';
    document.body.appendChild(overlay);
    document.body.classList.add('spotlight-on');

    window.addEventListener('mousemove', (e) => {
      const x = (e.clientX / window.innerWidth) * 100;
      const y = (e.clientY / window.innerHeight) * 100;
      overlay.style.setProperty('--sx', `${x}%`);
      overlay.style.setProperty('--sy', `${y}%`);
    }, { passive: true });
  }

  // ---------- Shopify dynamic re-init ----------
  function bindShopifyReinit() {
  let raf = 0;

  const reinit = () => {
    autoTagCommon();
    initReveals(document);
  };

  const schedule = () => {
    if (raf) return;
    raf = requestAnimationFrame(() => {
      raf = 0;
      reinit();
    });
  };

  document.addEventListener('shopify:section:load', schedule);
  document.addEventListener('shopify:section:reorder', schedule);
  document.addEventListener('shopify:section:select', schedule);

  const mo = new MutationObserver(schedule);
  mo.observe(document.documentElement, { childList: true, subtree: true });
}


  // ---------- Boot ----------
  document.addEventListener('DOMContentLoaded', () => {
    boot();
    autoTagCommon();
    initReveals(document);
    initProgressBar();
    initHeaderSmartHide();
    initBackToTop();
    initSpotlight();
    bindShopifyReinit();
  });
})();
